import { linkLibraryValues } from "@redux/index";
import { EMPTY_GUID } from "@utils/helpers";
import { InformationType } from "@utils/optionsSets/OptionSets";
import { createMultipleInteractions, linkInteraction } from "features";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getInteractionSelector, memoizedSwimlanes } from "redux/memoizedSelectors";
import PriorityDropdown from "shared/dropdowns/cx-dropdown/PriorityDropdown";
import StatusDropdown from "shared/dropdowns/cx-dropdown/StatusDropdown";
import DepartmentDropdown from "shared/dropdowns/library-dropdown/DepartmentDropdown";
import InsightTypeDropdown from "shared/dropdowns/library-dropdown/InsightTypeDropdown";
import SystemDropdown from "shared/dropdowns/library-dropdown/SystemDropdown";
import TouchpointDropdown from "shared/dropdowns/library-dropdown/TouchpointDropdown";
import GroupDropdown from "shared/dropdowns/library-dropdown/group/GroupDropdown";
import PersonaDropdown from "shared/dropdowns/library-dropdown/persona/PersonaDropdown";
import TagsDropdown from "shared/dropdowns/library-dropdown/tags/TagsDropdown";
import { ActionPopOver } from "shared/popovers/ActionsPopOver";
import { Borders } from "shared/ui-basics/index";
import { v4 as uuidv4 } from "uuid";
import { Property, Score } from "../../../features/journey-map/modals/PersonaInteractions/components";
import MainRoute from "./MainRoute";

function PropertyManager({ item, updateFunction, mode, defaultRows, ...props }) {
	const [currentItem, setCurrentItem] = useState(item);
	const [rows, setRows] = useState(defaultRows);
	const fixedRows = ["persona", "sentiment", "insightType", "alternativeRoute", "mainRoute", "status", "priority"];
	const { t } = useTranslation();
	const [modifiedAttribNames, setModifiedAttribNames] = useState([]);

	useEffect(() => {
		if (JSON.stringify(currentItem) !== JSON.stringify(item)) {
			updateFunction({ item: currentItem, modifiedAttribNames });
			setModifiedAttribNames([]);
		}
	}, [currentItem]);

	const handleChangeRows = useCallback((id, remove) => {
		let update;
		if (remove) {
			const updatedDisplayed = rows.displayed.filter((col) => col !== id);
			const updatedExtra = [...rows.extra, id];
			update = { ...rows, displayed: updatedDisplayed, extra: updatedExtra };
		} else {
			const updatedDisplayed = [...rows.displayed, id];
			const updatedExtra = rows.extra.filter((col) => col !== id);
			update = { ...rows, displayed: updatedDisplayed, extra: updatedExtra };
		}
		setRows(update);
		setModifiedAttribNames((prev) => [...prev, "json_property"]);
		setCurrentItem((prev) => ({ ...prev, jsonProperty: JSON.stringify(update) }));

	}, [currentItem, props]);
	const propertyDetails = GetPropertyDetails({ item, currentItem, setCurrentItem, mode, updateFunction, setModifiedAttribNames, ...props });

	const hidedRows = useMemo(() => GetHidedProperties(handleChangeRows, rows, t), [rows]);
	const allAdded = useMemo(() => hidedRows.find((d) => d.disabled === false) === undefined, [hidedRows]);
	return (
		<React.Fragment>
			{rows.displayed.map((row) => {
				return <Property key={row} id={row} className="mt-3" {...propertyDetails[row]} mode={mode} onRemove={!fixedRows.includes(row) && handleChangeRows} />;
			})}
			{allAdded ? (
				<div className="my-3" />
			) : (
				<Borders top={!mode.IsViewer()} bottom={!mode.IsViewer()} className="w-50 p-1 my-3">
					{!mode.IsViewer() && hidedRows?.length > 0 && <ActionPopOver label="ADD_PROPERTY" variant="light-link" actions={hidedRows} />}
				</Borders>
			)}
		</React.Fragment>
	);
}

const GetPropertyDetails = ({ item, currentItem, setCurrentItem, recordType, mode, updateFunction, setModifiedAttribNames, stageId, linkFunction, unlinkFunction }) => {
	const { personaIdStr, cjmIdStr } = useLocation().search.getAllMapsParams();
	const swAdvancedSys = useSelector(memoizedSwimlanes, shallowEqual)?.filter((swimlane) => swimlane?.type === InformationType.Systems);
	const swAdvancedDep = useSelector(memoizedSwimlanes, shallowEqual)?.filter((swimlane) => swimlane?.type === InformationType.Departments);
	const swAdvancedInteractions = useSelector(memoizedSwimlanes, shallowEqual)?.filter((swimlane) => swimlane?.type === InformationType.AdvancedInteractions);
	const interactions = useSelector(getInteractionSelector(personaIdStr, stageId));
	const all_insightsTypes = useSelector((s) => _.get(s, "libraries.insightTypes", []));
	const current_user = useSelector((state) => state.maps.currentUser);
	const [localItem, setLocalItem] = useState(item);
	const currentInsightType = all_insightsTypes?.find((type) => type.id === currentItem?.typeId);
	const handleChangeType = (id) => {
		const newInteraction = { ...currentItem, typeId: id };
		setModifiedAttribNames((prev) => [...prev, "type"]);

		setCurrentItem((prev) => ({ ...prev, typeId: id }));
		// updateFunction({ interaction: newInteraction, modifiedAttribNames: ["type"] });
	};

	const linkInsightToInteraction = (id) => {
		if (cjmIdStr && stageId) {
			const getInteraction = interactions?.filter((inter) => inter?.channelId === id);
			if (getInteraction && getInteraction?.length > 0) {
				getInteraction?.forEach((inter) => {
					linkInteraction({
						recIds: [currentItem.id],
						stageId,
						itemId: inter.id,
						attribute: "linkedInsights",
						recEntityName: "cem_insight",
					});
				});
			} else {
				//create persona interaction
				const newGuid = uuidv4();
				createMultipleInteractions({ personaInteractionsIds: { [id]: newGuid }, personaIdStr, cjmIdStr, ownerId: EMPTY_GUID, stageId: currentItem?.stageId, modifiedBy: current_user?.fullName });
				linkInteraction({
					recIds: [currentItem.id],
					stageId,
					itemId: newGuid,
					attribute: "linkedInsights",
					recEntityName: "cem_insight",
				});
			}
		}
	};
	const handleChangeElement = ({ id, attribute, mode, entityType, prefix }) => {
		let update;
		if (mode === "delete") {
			update = localItem?.[attribute].filter((s) => s !== id);
		} else {
			update = [...(localItem?.[attribute] || []), id];
		}

		const updatedItem = { ...localItem, [attribute]: update };
		setLocalItem(updatedItem);

		if (mode === "delete") {
			unlinkFunction({ itemId: currentItem.id, recIds: [id], stageId, attribute, recEntityName: entityType, prefixRelationships: prefix });
		} else {
			linkFunction({ itemId: currentItem.id, recIds: [id], stageId, attribute, recEntityName: entityType, prefixRelationships: prefix });
			if (attribute === "linkedTouchpoints" && recordType === "cem_insight" && swAdvancedInteractions) {
				//link TP on Insight
				linkInsightToInteraction(id);
			}
			if (attribute == "linkedSystems" && swAdvancedSys) {
				if (swAdvancedSys?.[0]) {
					const newId = uuidv4();
					linkLibraryValues({ swimLaneItemIds: [newId], libraryValueIds: [id], customSwimLaneId: swAdvancedSys?.[0]?.key, stageId, cjmId: cjmIdStr });
				}
			}
			if (attribute == "linkedDepartments" && swAdvancedDep) {
				if (swAdvancedDep?.[0]) {
					const newId = uuidv4();
					linkLibraryValues({ swimLaneItemIds: [newId], libraryValueIds: [id], customSwimLaneId: swAdvancedDep?.[0]?.key, stageId, cjmId: cjmIdStr });
				}
			}
		}
	};

	const handleChangeTagsGroups = (updated) => {
		const newInteraction = { ...currentItem, tags: updated.tags, groups: updated.groups };
		setModifiedAttribNames((prev) => [...prev, ...["tags", "groups"]]);

		setCurrentItem((prev) => ({ ...prev, tags: updated.tags, groups: updated.groups }));
		// updateFunction({ interaction: newInteraction, modifiedAttribNames: ["tags", "groups"] });
	};

	const handleUpdateStatusPriority = ({ id, attribute }) => {
		const newInteraction = { ...currentItem, [attribute]: id };
		setModifiedAttribNames((prev) => [...prev, attribute]);
		setCurrentItem((prev) => ({ ...prev, [attribute]: id }));
		// updateFunction({ interaction: newInteraction, modifiedAttribNames: [attribute] });
	};

	const properties = {
		persona: {
			icon: "PERSONA",
			label: "ASPX_98",
			RenderElement: () => (
				<PersonaDropdown
					object={localItem}
					recordType={recordType}
					link={(id) => handleChangeElement({ id, attribute: "linkedPersonas", entityType: "cem_persona" })}
					unlink={(id) => handleChangeElement({ id, attribute: "linkedPersonas", entityType: "cem_persona", mode: "delete" })}
					isSwitchDisabled={mode?.IsViewer()}
				/>
			),
		},
		sentiment: {
			label: "JS_79",
			icon: "face-smile",
			RenderElement: () => <Score interaction={currentItem} setInteraction={setCurrentItem} updateFunction={updateFunction} disabled={mode.IsViewer()} />,
		},
		mainRoute: {
			label: "MAIN_ROUTE",
			icon: "arrows-turn-right",
			RenderElement: () => (
				<MainRoute interaction={currentItem} setInteraction={setCurrentItem} stageId={stageId} mode={mode} calculatedRouteAttrib={"mainRoute"} forcedRouteAttrib={"forcedMainRoute"} />
			),
		},
		alternativeRoute: {
			label: "ALTERNATIVE_ROUTE",
			icon: "arrows-turn-right",
			RenderElement: () => (
				<MainRoute
					interaction={currentItem}
					setInteraction={setCurrentItem}
					stageId={stageId}
					mode={mode}
					calculatedRouteAttrib={"alternativeRoute"}
					forcedRouteAttrib={"forcedAlternativeRoute"}
				/>
			),
		},
		tags: {
			label: "TAG",
			icon: "tag",
			RenderElement: () => <TagsDropdown object={currentItem} setObject={handleChangeTagsGroups} recordType={recordType} isSwitchDisabled={mode?.IsViewer()} />,
		},
		groups: {
			label: "GROUP",
			icon: "tag",
			RenderElement: () => <GroupDropdown object={currentItem} setObject={handleChangeTagsGroups} recordType={recordType} isSwitchDisabled={mode?.IsViewer()} size="xs" />,
		},
		departments: {
			label: "DEPARTMENTS",
			icon: "DEPARTMENT_DUOTONE",
			RenderElement: () => (
				<DepartmentDropdown
					object={localItem}
					link={(id) => handleChangeElement({ id, attribute: "linkedDepartments", entityType: "cem_libraryvalue", prefix: "department" })}
					unlink={(id) => handleChangeElement({ id, attribute: "linkedDepartments", mode: "delete", entityType: "cem_libraryvalue", prefix: "department" })}
					isSwitchDisabled={mode?.IsViewer()}
				/>
			),
		},
		systems: {
			label: "SYSTEMS",
			icon: "SYSTEM_DUOTONE",
			RenderElement: () => (
				<SystemDropdown
					object={localItem}
					link={(id) => handleChangeElement({ id, attribute: "linkedSystems", entityType: "cem_libraryvalue", prefix: "system" })}
					unlink={(id) => handleChangeElement({ id, attribute: "linkedSystems", mode: "delete", entityType: "cem_libraryvalue", prefix: "system" })}
					isSwitchDisabled={mode?.IsViewer()}
				/>
			),
		},
		linkedTouchpoints: {
			label: "LINKED_TOUCHPOINTS",
			icon: "circle-dot",
			RenderElement: () => (
				<TouchpointDropdown
					object={localItem}
					link={(id) => handleChangeElement({ id, attribute: "linkedTouchpoints", entityType: "cem_touchpoints" })}
					unlink={(id) => handleChangeElement({ id, attribute: "linkedTouchpoints", mode: "delete", entityType: "cem_touchpoints" })}
					isSwitchDisabled={mode?.IsViewer()}
					stageId={recordType === "cem_insight" && currentItem?.stageId}
				/>
			),
		},
		insightType: {
			icon: "HASHTAG",
			label: "JS_78",
			RenderElement: () => (
				<InsightTypeDropdown
					object={currentInsightType?.id ? [currentInsightType?.id] : []}
					link={handleChangeType}
					unlink={() => handleChangeType({ id: EMPTY_GUID })}
					isSwitchDisabled={mode.IsViewer()}
				/>
			),
		},
		status: {
			icon: "circle",
			label: "ASPX_222",
			RenderElement: () => (
				<StatusDropdown
					object={currentItem}
					attributeName="statusId"
					recordType="insight"
					link={(id) => handleUpdateStatusPriority({ id, attribute: "statusId" })}
					unlink={() => handleUpdateStatusPriority({ id: EMPTY_GUID, attribute: "statusId" })}
					isSwitchDisabled={mode.IsViewer()}
				/>
			),
		},
		priority: {
			icon: "arrow-up-big-small",
			label: "PRIORITY",
			RenderElement: () => (
				<PriorityDropdown
					object={currentItem}
					attributeName="priorityId"
					recordType="insight"
					link={(id) => handleUpdateStatusPriority({ id, attribute: "priorityId" })}
					unlink={() => handleUpdateStatusPriority({ id: EMPTY_GUID, attribute: "priorityId" })}
					isSwitchDisabled={mode.IsViewer()}
				/>
			),
		},
	};

	return properties;
};

const GetHidedProperties = (handleChangeRows, rows, t) => {
	return [
		{ id: "groups", handleClick: () => handleChangeRows("groups"), icon: "GROUP_DUOTONE", text: t("GROUPS"), itemClasses: "d-flex align-items-center" },
		{ id: "systems", handleClick: () => handleChangeRows("systems"), icon: "window", text: t("SYSTEMS"), itemClasses: "d-flex align-items-center" },
		{ id: "departments", handleClick: () => handleChangeRows("departments"), icon: "user-group", text: t("DEPARTMENTS"), itemClasses: "d-flex align-items-center" },
		{ id: "linkedTouchpoints", handleClick: () => handleChangeRows("linkedTouchpoints"), icon: "LINK_DUOTONE", text: t("LINKED_TOUCHPOINTS"), itemClasses: "d-flex align-items-center" },
		{ id: "tags", handleClick: () => handleChangeRows("tags"), icon: "TAG", text: t("TAGS"), itemClasses: "d-flex align-items-center" },
	].map((action) => {
		return { ...action, disabled: rows.displayed.includes(action.id) };
	});
};

PropertyManager.displayName = "PropertyManager";
PropertyManager.propTypes = {
	item: PropTypes.object,
	updateFunction: PropTypes.func,
	mode: PropTypes.string,
	linkFunction: PropTypes.func,
	unlinkFunction: PropTypes.func,
	recordType: PropTypes.string,
	defaultRows: PropTypes.object,
};
export default PropertyManager;
